/**
 * GanttCanvas — the hand-SVG multi-track Gantt for W7 (no Plotly, no Gantt lib).
 * Draws the track bands, week/day gridlines, the Today line, the look-ahead
 * band, and every placed node: plan bars as draggable dashed outlines with grab
 * handles, actual/npt bars filled, milestones as diamonds. Pure presentation —
 * the model computes every pixel; this maps them to SVG. Selecting or dragging a
 * plan bar is bubbled up so the screen owns the editable node list.
 *
 * Drag: a plan bar can be dragged horizontally to re-time it (whole-day snap);
 * the screen commits the shift on drag end. Everything else is click-to-select.
 */
import { useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import type { TimelineNode } from "@api/types";
import {
  type LaidTrack,
  type PlacedNode,
  type Scale,
  axisTicks,
  canvasHeight,
  lookaheadBand,
  opVar,
  todayX,
  type Zoom,
} from "./timelineModel";

export interface GanttCanvasProps {
  laid: LaidTrack[];
  placed: PlacedNode[];
  scale: Scale;
  zoom: Zoom;
  todayISO: string;
  lookaheadWeeks: number;
  showToday: boolean;
  selectedId: string | null;
  ariaLabel: string;
  /** Locale tag for the axis date labels. */
  intlTag: string;
  onSelect: (node: TimelineNode) => void;
  /** Commit a whole-day time shift on a plan bar (drag end). */
  onShiftDays: (id: string, deltaDays: number) => void;
}

/** Format an ISO day as a short axis label (e.g. "4 ago" / "Aug 4"). */
function axisLabel(iso: string, intlTag: string): string {
  const ms = Date.parse(`${iso}T00:00:00Z`);
  if (!Number.isFinite(ms)) return iso;
  return new Intl.DateTimeFormat(intlTag, { day: "numeric", month: "short", timeZone: "UTC" }).format(
    new Date(ms),
  );
}

export function GanttCanvas({
  laid,
  placed,
  scale,
  zoom,
  todayISO,
  lookaheadWeeks,
  showToday,
  selectedId,
  ariaLabel,
  intlTag,
  onSelect,
  onShiftDays,
}: GanttCanvasProps) {
  const height = canvasHeight(laid);
  const ticks = axisTicks(scale, zoom);
  const tX = todayX(todayISO, scale);
  const band = lookaheadBand(todayISO, lookaheadWeeks, scale);
  const axisY = height - 20;

  // Only label every Nth tick so day-zoom doesn't crowd; keep first + last.
  const labelEvery = zoom === "day" ? 7 : zoom === "week" ? 2 : 1;

  // Drag state — the plan bar being dragged + the live pixel delta.
  const svgRef = useRef<SVGSVGElement | null>(null);
  const [drag, setDrag] = useState<{ id: string; startX: number; dx: number } | null>(null);

  function beginDrag(e: ReactPointerEvent, node: TimelineNode) {
    if (node.kind !== "plan") return;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    setDrag({ id: node.id, startX: e.clientX, dx: 0 });
  }
  function moveDrag(e: ReactPointerEvent) {
    if (!drag) return;
    setDrag({ ...drag, dx: e.clientX - drag.startX });
  }
  function endDrag(e: ReactPointerEvent) {
    if (!drag) return;
    (e.target as Element).releasePointerCapture?.(e.pointerId);
    const deltaDays = Math.round(drag.dx / scale.pxPerDay);
    if (deltaDays !== 0) onShiftDays(drag.id, deltaDays);
    setDrag(null);
  }

  return (
    <div className="canvas-scroll">
      <svg
        ref={svgRef}
        className="gantt-svg"
        width={scale.width + 4}
        height={height}
        viewBox={`0 0 ${scale.width + 4} ${height}`}
        role="img"
        aria-label={ariaLabel}
        onPointerMove={moveDrag}
        onPointerUp={endDrag}
        onPointerLeave={endDrag}
      >
        {/* look-ahead band (right of Today) */}
        {showToday && band.width > 0 && (
          <rect className="lookahead-band" x={band.x} y={4} width={band.width} height={axisY - 4} />
        )}

        {/* vertical gridlines at ticks */}
        <g className="gridline">
          {ticks.map((tk) => (
            <line key={`g-${tk.iso}`} x1={tk.x} y1={4} x2={tk.x} y2={axisY} />
          ))}
        </g>

        {/* track row separators */}
        <g className="rowsep">
          {laid.map((t) => (
            <line key={`sep-${t.id}`} x1={0} y1={t.y + t.height} x2={scale.width} y2={t.y + t.height} />
          ))}
        </g>

        {/* Today line */}
        {showToday && (
          <>
            <line className="today-line" x1={tX} y1={4} x2={tX} y2={axisY} />
            <text className="today-lbl" x={tX + 4} y={16}>
              {axisLabel(todayISO, intlTag)}
            </text>
          </>
        )}

        {/* nodes */}
        <g>
          {placed.map((p) => (
            <NodeShape
              key={p.node.id}
              placed={p}
              selected={p.node.id === selectedId}
              dragDx={drag && drag.id === p.node.id ? drag.dx : 0}
              onSelect={onSelect}
              onBeginDrag={beginDrag}
            />
          ))}
        </g>

        {/* x-axis */}
        <line className="axis" x1={0} y1={axisY} x2={scale.width} y2={axisY} />
        <g className="axis-lbl">
          {ticks.map((tk, i) =>
            i % labelEvery === 0 || i === ticks.length - 1 ? (
              <text key={`al-${tk.iso}`} x={tk.x} y={axisY + 14} textAnchor="middle">
                {axisLabel(tk.iso, intlTag)}
              </text>
            ) : null,
          )}
        </g>
      </svg>
    </div>
  );
}

/** One node rendered as its shape (plan outline / filled bar / diamond). */
function NodeShape({
  placed,
  selected,
  dragDx,
  onSelect,
  onBeginDrag,
}: {
  placed: PlacedNode;
  selected: boolean;
  dragDx: number;
  onSelect: (n: TimelineNode) => void;
  onBeginDrag: (e: ReactPointerEvent, n: TimelineNode) => void;
}) {
  const { node, x, width, y, height } = placed;
  const color = `var(${opVar(node.op)})`;
  const cy = y + height / 2;
  const dx = dragDx;

  // Milestone → diamond marker.
  if (node.kind === "milestone") {
    const s = Math.min(9, height);
    return (
      <g
        className="milestone"
        role="button"
        tabIndex={0}
        aria-label={node.label}
        onClick={() => onSelect(node)}
        onKeyDown={(e) => (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onSelect(node))}
      >
        <rect x={x - s / 2 + dx} y={cy - s / 2} width={s} height={s} transform={`rotate(45 ${x + dx} ${cy})`} />
      </g>
    );
  }

  const isPlan = node.kind === "plan";
  const isNpt = node.kind === "npt";
  const fill = isPlan ? "none" : isNpt ? "var(--crit)" : color;
  const stroke = isPlan ? color : "none";

  return (
    <g>
      <rect
        className={
          "node-bar" +
          (isPlan ? " plan-bar" : "") +
          (selected ? " selected" : "")
        }
        x={x + dx}
        y={y}
        width={Math.max(width, 4)}
        height={height}
        rx={3}
        fill={fill}
        stroke={stroke}
        strokeWidth={isPlan ? 1.8 : 0}
        strokeDasharray={isPlan ? "4,3" : undefined}
        tabIndex={isPlan ? 0 : -1}
        role={isPlan ? "button" : undefined}
        aria-label={node.label}
        style={{ cursor: isPlan ? "grab" : "default" }}
        onClick={() => onSelect(node)}
        onPointerDown={(e) => onBeginDrag(e, node)}
        onKeyDown={(e) =>
          isPlan && (e.key === "Enter" || e.key === " ") && (e.preventDefault(), onSelect(node))
        }
      />
      {width > 40 && (
        <text
          className="node-lbl"
          x={x + dx + Math.max(width, 4) / 2}
          y={y + height / 2 + 3.5}
          textAnchor="middle"
          fill={isPlan ? "var(--head)" : "var(--brand-contrast)"}
          pointerEvents="none"
        >
          {node.label}
        </text>
      )}
    </g>
  );
}
